import * as React from 'react';
import { type FeatureFlagUser, toFeatureFlagContext } from '../provider/provider';
import { useFeatureFlags } from './use-feature-flags';

/** How many times a failed identify is tried again, waiting 1s, then 2s, then 4s. */
const MAX_IDENTIFY_RETRIES = 3;
const IDENTIFY_RETRY_DELAY_MS = 1000;

/**
 * Whether LaunchDarkly already has this user, organization and workspace. The email is left out: auth
 * only learns it at login, so after a reload it is missing from the auth state but not from the
 * user LaunchDarkly loaded with, and it targets no flag.
 */
function isSameFeatureFlagUser(current: Partial<FeatureFlagUser>, user: FeatureFlagUser) {
  return (
    current.key === user.key &&
    current.organizationId === user.organizationId &&
    current.workspaceId === user.workspaceId
  );
}

/**
 * Tells LaunchDarkly when the signed-in user, organization or workspace changes. The provider reads
 * the user once, at page load, and login and SSO move on without a reload, so flags targeted at an
 * organization would otherwise answer for whoever was signed in when the page loaded.
 */
export function useIdentifyFeatureFlagUser(user: FeatureFlagUser) {
  const { ldClient } = useFeatureFlags();
  const { key, email, organizationId, workspaceId } = user;

  React.useEffect(() => {
    if (!ldClient) {
      return;
    }
    const current = (ldClient.getContext() ?? {}) as Partial<FeatureFlagUser>;
    const nextUser = { key, email, organizationId, workspaceId };
    if (isSameFeatureFlagUser(current, nextUser)) {
      return;
    }
    // The same user switching organization keeps the email LaunchDarkly already has.
    const nextEmail = email ?? (current.key === key ? current.email : undefined);
    const context = toFeatureFlagContext({ ...nextUser, email: nextEmail });

    // A failed identify keeps the previous user's flags, so it is tried again a few times. After that
    // the flags stay as they were until the user changes or the page reloads.
    let isCurrent = true;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const identify = (attempt: number) => {
      ldClient.identify(context).catch(() => {
        if (isCurrent && attempt < MAX_IDENTIFY_RETRIES) {
          retryTimer = setTimeout(() => identify(attempt + 1), IDENTIFY_RETRY_DELAY_MS * 2 ** attempt);
        }
      });
    };
    identify(0);

    return () => {
      isCurrent = false;
      clearTimeout(retryTimer);
    };
  }, [ldClient, key, email, organizationId, workspaceId]);
}
